import { useEffect, useState } from 'react';
import { Sun, Moon, Monitor } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import {
  getStoredThemePreference,
  applyThemePreference,
  resolveTheme,
  type ThemePreference,
  type ResolvedTheme,
} from '../lib/theme';

function ThemeIcon({ preference, resolved }: { preference: ThemePreference; resolved: ResolvedTheme }) {
  if (preference === 'system') return <Monitor className="w-4 h-4" />;
  return resolved === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />;
}

export function ThemeToggle() {
  const [preference, setPreference] = useState<ThemePreference>(getStoredThemePreference);
  const resolved = resolveTheme(preference);

  useEffect(() => {
    applyThemePreference(preference);
  }, [preference]);

  useEffect(() => {
    if (preference !== 'system') return;
    const mq = window.matchMedia('(prefers-color-scheme: light)');
    const onChange = () => applyThemePreference('system');
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [preference]);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        aria-label={`Theme: ${preference === 'system' ? 'system' : resolved}`}
        className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-transparent text-text-secondary transition-colors hover:bg-state-hover hover:text-text-primary focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50"
      >
        <ThemeIcon preference={preference} resolved={resolved} />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[8rem]">
        <DropdownMenuItem
          onClick={() => setPreference('system')}
          className={preference === 'system' ? 'bg-state-hover' : ''}
        >
          <Monitor className="w-3.5 h-3.5 mr-2" />
          System
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => setPreference('light')}
          className={preference === 'light' ? 'bg-state-hover' : ''}
        >
          <Sun className="w-3.5 h-3.5 mr-2" />
          Light
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => setPreference('dark')}
          className={preference === 'dark' ? 'bg-state-hover' : ''}
        >
          <Moon className="w-3.5 h-3.5 mr-2" />
          Dark
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
